import React from 'react';
import { useGetSeatsIOEvent } from 'hooks/useGetSeatsIOEvent';
import { useGetSeatsIOEventReport } from 'hooks/useGetSeatsIOEventReport';
import { useGetSeatsChart } from 'hooks/useGetSeatsChart';
import { SeatsIOCategory } from 'services/Seats';
import { useTranslation } from 'react-i18next';
import styles from './SeatsCategories.module.css';

interface SeatsCategoriesProps {
	eventKey: string | null | undefined;
	chartKey: string | null | undefined;
	selectedCategory?: string;
	onCategorySelect?: (category: SeatsIOCategory) => void;
	/**
	 * When true, category rows are non-interactive: clicks are ignored, cursor is default, and
	 * a tooltip explains the lock. The currently selected category is still visually highlighted.
	 * Used for seated events where the binding is fixed at creation.
	 */
	readOnly?: boolean;
}

/** Minimal shape of a seats chart needed to render the chart card. */
interface ChartCardDetails {
	name: string;
	publishedVersionThumbnailUrl?: string | null;
	venueType?: string | null;
}

export const SeatsCategories: React.FC<SeatsCategoriesProps> = ({
	eventKey,
	chartKey,
	selectedCategory,
	onCategorySelect,
	readOnly = false,
}) => {
	const { t } = useTranslation();
	const { data: seatsEvent, isLoading, error } = useGetSeatsIOEvent(eventKey);
	const { data: availabilityReport } = useGetSeatsIOEventReport(eventKey);
	const { data: chartDetails, isLoading: chartLoading } = useGetSeatsChart(chartKey);

	const getPlaceCount = (categoryLabel: string): number => {
		if (!availabilityReport?.available?.byCategoryLabel) return 0;
		return availabilityReport.available.byCategoryLabel[categoryLabel] || 0;
	};

	// The chart card (title + thumbnail + venue) renders identically in the no-categories branch and the
	// main view — keep it in one place so a copy change lands once.
	const ChartCard: React.FC<{ chart: ChartCardDetails }> = ({ chart }) => (
		<div className={styles.chartContent}>
			<h4 className={styles.chartTitle}>{chart.name}</h4>
			{chart.publishedVersionThumbnailUrl && (
				<img
					className={styles.chartThumbnail}
					src={chart.publishedVersionThumbnailUrl}
					alt={t('seatsCategories-chart-alt', '{{name}} seating chart', { name: chart.name })}
				/>
			)}
			<div className={styles.chartInfo}>
				{t('seatsCategories-venue-label', 'Venue:')} {chart.venueType || t('seatsCategories-venue-default', 'Standard')}
			</div>
		</div>
	);

	if (!eventKey && !chartKey) {
		return null;
	}

	if (isLoading || (chartKey && chartLoading)) {
		return (
			<div className={styles.categoriesSection}>
				<h3 className={styles.categoriesTitle}>{t('seatsCategories-title', 'Seat Categories')}</h3>
				<div className={styles.loadingState}>{t('seatsCategories-loading', 'Loading seat categories...')}</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className={styles.categoriesSection}>
				<h3 className={styles.categoriesTitle}>{t('seatsCategories-title', 'Seat Categories')}</h3>
				<div className={styles.errorState}>{t('seatsCategories-error', 'Failed to load seat categories.')}</div>
			</div>
		);
	}

	if (!seatsEvent?.categories || seatsEvent.categories.length === 0) {
		if (chartDetails) {
			return (
				<div className={styles.categoriesSection}>
					<h3 className={styles.categoriesTitle}>{t('seatsCategories-chart-title', 'Seating Chart')}</h3>
					<div className={styles.chartContainer} style={{ maxWidth: '100%' }}>
						<ChartCard chart={chartDetails} />
					</div>
				</div>
			);
		}

		return (
			<div className={styles.categoriesSection}>
				<h3 className={styles.categoriesTitle}>{t('seatsCategories-title', 'Seat Categories')}</h3>
				<div className={styles.loadingState}>
					{t('seatsCategories-no-categories', 'No categories available for this seating chart.')}
				</div>
			</div>
		);
	}

	return (
		<div className={styles.categoriesSection}>
			<h3 className={styles.categoriesTitle}>{t('seatsCategories-title', 'Seat Categories')}</h3>
			<div className={styles.categoriesContainer}>
				<div className={styles.categoriesList}>
					{seatsEvent.categories.map((category) => {
						const isSelected = String(selectedCategory === String(category.key));
						const placesLabel = t('seatsCategories-places', '{{count}} places', {
							count: getPlaceCount(category.label),
						});
						const inner = (
							<>
								<div className={styles.categoryColor} style={{ backgroundColor: category.color }} />
								<div className={styles.categoryInfo}>
									<div className={styles.categoryLabel}>{category.label}</div>
									<div className={styles.categoryPlaces}>{placesLabel}</div>
								</div>
							</>
						);

						// Interactive (non-seated) rows are real <button>s so keyboard/SR users can select a
						// category; read-only (seated) rows are a non-interactive <div> with the lock tooltip.
						return readOnly ? (
							<div
								key={category.key}
								className={styles.categoryItem}
								data-selected={isSelected}
								data-readonly="true"
								aria-disabled
								title={t(
									'seatsCategories-readonly-tooltip',
									'Bound at creation — manage categories in the seats chart.'
								)}
							>
								{inner}
							</div>
						) : (
							<button
								key={category.key}
								type="button"
								className={styles.categoryItem}
								data-selected={isSelected}
								data-readonly="false"
								onClick={() => onCategorySelect?.(category)}
							>
								{inner}
							</button>
						);
					})}
				</div>
				<div className={styles.chartContainer}>
					{chartDetails ? (
						<ChartCard chart={chartDetails} />
					) : (
						<div className={styles.chartPlaceholder}>
							{chartKey
								? t('seatsCategories-chart-loading', 'Loading chart...')
								: t('seatsCategories-chart-unavailable', 'No seating chart available')}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
